import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

type SpyItem =
  | { kind: 'section'; id: string; label: string }
  | { kind: 'route'; to: string; label: string };

const ITEMS: SpyItem[] = [
  { kind: 'section', id: 'features', label: 'Features' },
  { kind: 'section', id: 'how-it-works', label: 'How it works' },
  { kind: 'section', id: 'trust', label: 'Trust' },
  { kind: 'route', to: '/pricing', label: 'Pricing' },
];

const DESKTOP_MQ = '(min-width: 1024px)';

const SectionSpyNav: React.FC = () => {
  const [desktop, setDesktop] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(DESKTOP_MQ).matches : false,
  );
  const [activeId, setActiveId] = useState<string>('features');
  const [hovered, setHovered] = useState<string | null>(null);

  useEffect(() => {
    const mq = window.matchMedia(DESKTOP_MQ);
    const onChange = () => setDesktop(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    if (!desktop) return;

    const sectionIds = ITEMS.filter((i): i is Extract<SpyItem, { kind: 'section' }> => i.kind === 'section')
      .map((i) => i.id);
    const elements = sectionIds
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => !!el);

    if (elements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]?.target?.id) {
          setActiveId(visible[0].target.id);
        }
      },
      {
        // Offset for fixed navbar (~72px) and prefer mid-viewport activation
        rootMargin: '-20% 0px -55% 0px',
        threshold: [0, 0.25, 0.5, 0.75, 1],
      },
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [desktop]);

  if (!desktop) return null;

  const scrollTo = (id: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveId(id);
  };

  return (
    <nav
      aria-label="Page sections"
      style={{
        position: 'fixed',
        right: 20,
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 900,
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        alignItems: 'flex-end',
      }}
    >
      {ITEMS.map((item) => {
        const key = item.kind === 'section' ? item.id : item.to;
        const isActive = item.kind === 'section' && activeId === item.id;
        const showLabel = hovered === key || isActive;

        const rowStyle: React.CSSProperties = {
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          textDecoration: 'none',
          color: 'inherit',
          cursor: 'pointer',
        };

        const label = (
          <span
            style={{
              fontSize: '0.72rem',
              fontWeight: 600,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              color: isActive ? '#f9fafb' : '#94a3b8',
              opacity: showLabel ? 1 : 0,
              transform: showLabel ? 'translateX(0)' : 'translateX(6px)',
              transition: 'opacity 0.2s, transform 0.2s, color 0.2s',
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
              background: 'rgba(15,23,42,0.85)',
              border: '1px solid rgba(148,163,184,0.15)',
              borderRadius: 8,
              padding: '4px 10px',
            }}
          >
            {item.label}
          </span>
        );

        const dot = (
          <span
            aria-hidden
            style={{
              width: isActive ? 10 : 8,
              height: isActive ? 10 : 8,
              borderRadius: '50%',
              background: isActive
                ? 'linear-gradient(135deg, #2563eb, #22c55e)'
                : 'rgba(148,163,184,0.45)',
              boxShadow: isActive ? '0 0 0 3px rgba(37,99,235,0.25)' : 'none',
              transition: 'width 0.2s, height 0.2s, background 0.2s, box-shadow 0.2s',
              flexShrink: 0,
            }}
          />
        );

        if (item.kind === 'route') {
          return (
            <Link
              key={key}
              to={item.to}
              aria-label={item.label}
              style={rowStyle}
              onMouseEnter={() => setHovered(key)}
              onMouseLeave={() => setHovered(null)}
            >
              {label}
              {dot}
            </Link>
          );
        }

        return (
          <a
            key={key}
            href={`#${item.id}`}
            aria-label={item.label}
            aria-current={isActive ? 'true' : undefined}
            style={rowStyle}
            onClick={scrollTo(item.id)}
            onMouseEnter={() => setHovered(key)}
            onMouseLeave={() => setHovered(null)}
          >
            {label}
            {dot}
          </a>
        );
      })}
    </nav>
  );
};

export default SectionSpyNav;
